import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import Canvas from './Canvas';
import PinModal from './PinModal';
import TextModal from './TextModal';
import SaveButton from './SaveButton';
import PinIcon from './PinIcon';
import NotFound from './NotFound';
import './App.css';

function ImageEditor() {
  const [image, setImage] = useState(null);
  const [pins, setPins] = useState([]);
  const [showPinModal, setShowPinModal] = useState(false);
  const [selectedPin, setSelectedPin] = useState(null);
  const [currentTool, setCurrentTool] = useState('select');
  const [color, setColor] = useState('#02414a');
  const [shapes, setShapes] = useState([]);
  const [placingPin, setPlacingPin] = useState(null);
  const [pendingPlacingPin, setPendingPlacingPin] = useState(null);
  const [thickness, setThickness] = useState(0.2);
  const [productPins, setProductPins] = useState([]); // New: product pin list
  const [width, setWidth] = useState(null);
  const [height, setHeight] = useState(null);
  const [loading, setLoading] = useState(false);
  const canvasRef = useRef(null);
  const [id, setId] = useState(null);
  const [types, setTypes] = useState('default');
  const [serverValue, setServerValue] = useState('');
  const [showRainbowPicker, setShowRainbowPicker] = useState(false);
  const [showTextModal, setShowTextModal] = useState(false);
  const [textPosition, setTextPosition] = useState(null);
  const [selectedText, setSelectedText] = useState(null);
  const [draggingText, setDraggingText] = useState(false);
  const [showNotFound, setShowNotFound] = useState(false);

  // Helper to get image size from base64
  function getImageSizeFromBase64(base64) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        resolve({
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
      };
      img.onerror = reject;
      img.src = base64;
    });
  }

  const convertImageToBase64 = useCallback(async (idParam, type, server) => {

    setLoading(true);
    let url = "https://dev.salusemr.com/api/index?action=convert_img_to_base64";
    if (server === "prod") {
      url = "https://salusemr.com/api/index?action=convert_img_to_base64";
    }
    try {
      const response = await axios.post(
        url,
        {
          type: type,
          id: idParam,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response) {
        console.log("response", response.data);
        setImage(response.data.image_base64);
        if (response.data.product_data) {
          setProductPins(response.data.product_data)
        }

        await getImageSizeFromBase64(response.data.image_base64).then(
          ({ width, height }) => {
            setWidth(width);
            setHeight(height);
            console.log("Width:", width);
            console.log("Height:", height);
          }
        );
      }
    } catch (error) {
      console.error("Error converting image:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Check URL parameters and load image on page render
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const idParam = urlParams.get('id');
    const typeParam = urlParams.get('type');
    const serverParam = urlParams.get('server');


    // Check if URL matches the exact valid format
    const isValidUrl = idParam && typeParam;

    if (!isValidUrl) {
      console.log("Invalid URL parameters. Only http://localhost:3000/?img_url=null&type=1&id=46 is valid.");
      setShowNotFound(true);
      return;
    }

    setId(idParam);
    setTypes(typeParam);
    setServerValue(serverParam); // Default to dev server for the valid URL

    if (idParam) {
      console.log("Loading image with ID:", idParam, "Type:", typeParam, "Server:", serverParam);
      convertImageToBase64(idParam, typeParam, serverParam);
    }
  }, [convertImageToBase64]);

  useEffect(() => {
    // ✅ window.self === window.top → means "this page is the TOP window"
    // ✅ If true => page is opened directly in browser tab (blocked unless mobile flag)
    const urlParams = new URLSearchParams(window.location.search);
    const isMobile = urlParams.get('ismobile');
    if (isMobile === 'true' || isMobile === '1') {
      return;
    }
    // if (window.self === window.top) {
    //   document.body.innerHTML =
    //     "<h2 style='color:red;text-align:center;'>Access Denied</h2>";
    // }
  }, []);

  // Close rainbow picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showRainbowPicker) {
        setShowRainbowPicker(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showRainbowPicker]);

  // Tool icon mapping
  const toolIcons = {
    drag: '👆',      // Pointer hand emoji for drag
    freehand: '✏️',  // Pencil emoji for freehand
    square: '⧈',    // Circled white square (more pronounced outline)
    circle: '◯',    // Large circle (outline)
    arrow: '➔',      // Heavy wide-headed rightwards arrow (outline)
    line: '─',      // Box drawings light horizontal (outline)
    text: '📝',      // Memo emoji for text
    pin: '📌',       // Pushpin emoji (final choice)
    undo: '↶',      // Anticlockwise top semicircle arrow (outline)
  };
  const toolOrder = ['freehand', 'square', 'circle', 'arrow', 'line', 'text', 'drag'];

  // Pass a custom setPin to PinModal for Add Pin
  const handleAddPin = (pin) => {
    const newPin = { ...pin, id: Date.now() };
    setProductPins(list => [...list, newPin]);
    setPlacingPin(newPin); // Ensure the next pin placed uses the chosen values
    setShowPinModal(false);
  };

  // Handle text operations

  const handleTextDrag = (textShape, newPos) => {
    if (draggingText && selectedText && selectedText.id === textShape.id) {
      setShapes(shapes => shapes.map(shape =>
        shape.id === textShape.id
          ? { ...shape, x: newPos.x, y: newPos.y }
          : shape
      ));
    }
  };

  const handleTextEdit = (textShape) => {
    setSelectedText(textShape);
    setTextPosition({ x: textShape.x, y: textShape.y });
    setShowTextModal(true);
  };

  const handleTextDelete = (textShape) => {
    setShapes(shapes => shapes.filter(shape => shape.id !== textShape.id));
    setSelectedText(null);
  };

  const handleTextRotation = (textShape, newRotation) => {
    setShapes(shapes => shapes.map(shape =>
      shape.id === textShape.id
        ? { ...shape, rotation: newRotation }
        : shape
    ));
  };

  // Show 404 page if required parameters are missing
  if (showNotFound) {
    return <NotFound />;
  }

  return (
    <div className="image-editor-app">
      {loading && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(255,255,255,0.7)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, color: '#02414a', fontWeight: 600 }}>
          Loading image...
        </div>
      )}
      {/* Remove Pin Preview UI */}
      {/* <PinPreview /> */}
      {/* Sidebar */}
      <div className="sidebar">
        {toolOrder.map(tool => (
          <div key={tool} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <button
              className={`tool-btn${currentTool === tool && !placingPin ? ' selected' : ''}`}
              title={tool.charAt(0).toUpperCase() + tool.slice(1)}
              onClick={() => {
                setCurrentTool(currentTool === tool ? '' : tool);
                setPlacingPin(null);
              }}
              disabled={!!placingPin}
            >
              {toolIcons[tool]}
            </button>
            <span style={{ fontSize: 12, color: currentTool === tool && !placingPin ? '#02414a' : '#888', marginTop: 2, fontWeight: 500, letterSpacing: 0.01 }}>{tool.charAt(0).toUpperCase() + tool.slice(1)}</span>
          </div>
        ))}
        {/* Add Pin button */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 24 }}>
          <button
            className={`tool-btn${placingPin ? ' selected' : ''}`}
            title="Add Pin"
            onClick={() => {
              if (placingPin) {
                setPlacingPin(null);
                setShowPinModal(false);
              } else {
                // Create empty pin directly without opening modal
                setPlacingPin({ dropdown: '', number: 1, color: '#73DE1B', rotation: 0 });
                setShowPinModal(true);
              }
            }}
            disabled={!!placingPin && showPinModal}
          >
            {toolIcons.pin}
          </button>
          <span style={{ fontSize: 12, color: placingPin ? '#02414a' : '#888', marginTop: 2, fontWeight: 500, letterSpacing: 0.01 }}>Add Pin</span>
        </div>
        {/* Undo button */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 24 }}>
          <button
            className="tool-btn"
            title="Undo"
            onClick={() => setShapes(shapes.slice(0, -1))}
            disabled={shapes.length === 0}
          >
            {toolIcons.undo}
          </button>
          <span style={{ fontSize: 12, color: shapes.length === 0 ? '#888' : '#e53935', marginTop: 2, fontWeight: 500, letterSpacing: 0.01 }}>Undo</span>
        </div>
      </div>
      {/* Main Content */}
      <div className="main-content">
        <div className="card">
          <h2 className="center-text" style={{ marginBottom: 18 }}>Image Editor</h2>
          {/* <ImagePicker setImage={setImage} /> */}
          {/* Quick options: shape icons, color, thickness slider */}
          <div className="quick-options">
            {/* Quick color buttons */}
            {[
              '#02414a', // blue
              '#e53935', // red
              '#43a047', // green
              '#fbc02d', // yellow
              '#222'     // black
            ].map(c => (
              <button
                key={c}
                className="quick-btn"
                style={{
                  background: c,
                  border: color === c ? '2px solid #02414a' : '2px solid #fff',
                  boxShadow: color === c ? '0 0 0 2px #02414a' : 'none',
                  marginLeft: 10,
                  marginRight: 0,
                  width: 28,
                  height: 28,
                  borderRadius: '4px'
                }}
                onClick={() => setColor(c)}
                disabled={!!placingPin}
                title={c}
              />
            ))}
            {/* Color picker as last button */}
            <div style={{ marginLeft: 10, marginRight: 12, position: 'relative' }}>
              <div
                title="Color picker"
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: '4px',
                  background: 'linear-gradient(45deg, #ff0000, #ff8000, #ffff00, #80ff00, #00ff00, #00ff80, #00ffff, #0080ff, #0000ff, #8000ff, #ff00ff, #ff0080)',
                  cursor: 'pointer',
                  border: '1px solid #ddd',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                onClick={(e) => {
                  if (placingPin) return;
                  e.stopPropagation();
                  setShowRainbowPicker(!showRainbowPicker);
                }}
              />
              {showRainbowPicker && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    zIndex: 1000,
                    background: '#fff',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    padding: '12px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    marginTop: '4px',
                    minWidth: '250px'
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div style={{ marginBottom: '12px', fontSize: '12px', fontWeight: '600', color: '#333' }}>
                    Color Picker
                  </div>
                  <input
                    type="color"
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                    style={{
                      width: '100%',
                      height: '40px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      padding: '0'
                    }}
                  />
                  <div style={{ marginTop: '8px', fontSize: '11px', color: '#666' }}>
                    Click to select any color
                  </div>
                </div>
              )}
            </div>
            {/* Thickness slider */}
            <label style={{ display: 'flex', alignItems: 'center', marginLeft: 8, minWidth: 120 }} title="Set thickness for shapes and freehand">
              <span style={{ fontSize: 13, color: '#888', marginRight: 6 }}>Thickness</span>
              <input
                type="range"
                min={0.1}
                max={1}
                step={0.1}
                value={thickness}
                onChange={e => setThickness(Number(e.target.value))}
                style={{ margin: '0 8px', flex: 1 }}
                disabled={!!placingPin}
              />
              <span style={{ fontSize: 13, color: '#02414a', minWidth: 24, textAlign: 'right' }}>{thickness.toFixed(1)}</span>
            </label>
          </div>
          <Canvas
            ref={canvasRef}
            image={image}
            currentTool={currentTool}
            color={color}
            pins={pins}
            setPins={setPins}
            showPinModal={showPinModal}
            setShowPinModal={setShowPinModal}
            selectedPin={selectedPin}
            setSelectedPin={pin => {
              if (placingPin) {
                setPendingPlacingPin(placingPin);
                setPlacingPin(null);
              }
              setSelectedPin(pin);
              if (pin && pin.color) setColor(pin.color);
            }}
            shapes={shapes}
            setShapes={setShapes}
            placingPin={placingPin}
            setPlacingPin={pin => {
              setPlacingPin(pin);
              setSelectedPin(null);
            }}
            pendingPlacingPin={pendingPlacingPin}
            setPendingPlacingPin={setPendingPlacingPin}
            thickness={thickness}
            width={width}
            height={height}
            showTextModal={showTextModal}
            setShowTextModal={setShowTextModal}
            setTextPosition={setTextPosition}
            selectedText={selectedText}
            setSelectedText={setSelectedText}
            draggingText={draggingText}
            setDraggingText={setDraggingText}
            handleTextDrag={handleTextDrag}
            handleTextEdit={handleTextEdit}
            handleTextDelete={handleTextDelete}
            handleTextRotation={handleTextRotation}
          />
          <SaveButton
            image={image}
            shapes={shapes}
            pins={pins}
            width={width}
            height={height}
            productList={productPins}
            id={id}
            types={types}
            serverValue={serverValue}
            setLoading={setLoading}
          />
        </div>
        {/* Product Pins List at bottom of app */}
        {productPins.length > 0 && (
          <div style={{
            position: 'fixed',
            left: 0,
            right: 0,
            bottom: 0,
            background: '#fff',
            borderTop: '1px solid #eee',
            boxShadow: '0 -2px 12px rgba(25,118,210,0.07)',
            padding: '12px 0',
            display: 'flex',
            justifyContent: 'center',
            gap: 18,
            zIndex: 3000,
          }}>
            {productPins.map(pin => (
              <div key={pin.id} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f5f5f5', borderRadius: 8, padding: '6px 14px', boxShadow: '0 1px 4px rgba(25,118,210,0.07)' }}>
                <PinIcon color={pin.color} number={pin.number} />
                <span style={{ color: '#02414a', fontWeight: 600, fontSize: 13 }}>{dropdownLabel(pin.dropdown)}</span>
                <button
                  style={{ background: 'none', border: 'none', color: '#e53935', fontSize: 16, cursor: 'pointer', marginLeft: 4 }}
                  onClick={() => {
                    setProductPins(list => list.filter(p => p.id !== pin.id));
                    setPins(pins => pins.filter(p => p.id !== pin.id));
                  }}
                  title="Remove"
                >✕</button>
              </div>
            ))}
          </div>
        )}
      </div>
      {showPinModal && (
        <PinModal
          pin={selectedPin || placingPin || { dropdown: '', number: 1, color: '#02414a', rotation: 0 }}
          mode={selectedPin ? 'edit' : 'add'}
          serverValue={serverValue}
          setPin={pin => {
            if (placingPin && !selectedPin) {
              handleAddPin(pin);
            } else if (selectedPin) {
              // Update the pin in both arrays
              const updatedPin = { ...selectedPin, ...pin };
              console.log('Updating pin:', selectedPin.id, 'with:', updatedPin);

              // Update pins array
              setPins(currentPins => {
                const newPins = currentPins.map(p => (p.id === selectedPin.id ? updatedPin : p));
                console.log('Updated pins:', newPins);
                return newPins;
              });

              // Update productPins array
              setProductPins(currentProductPins => {
                const newProductPins = currentProductPins.map(p => (p.id === selectedPin.id ? updatedPin : p));
                console.log('Updated productPins:', newProductPins);
                return newProductPins;
              });

              setShowPinModal(false);
              setSelectedPin(null);
              if (pendingPlacingPin) {
                setPlacingPin(pendingPlacingPin);
                setPendingPlacingPin(null);
              }
            }
          }}
          onDelete={pin => {
            setPins(pins.filter(p => p.id !== pin.id));
            setProductPins(productPins.filter(p => p.id !== pin.id));
            setShowPinModal(false);
            setSelectedPin(null);
            if (pendingPlacingPin) {
              setPlacingPin(pendingPlacingPin);
              setPendingPlacingPin(null);
            }
          }}
          onClose={() => {
            setShowPinModal(false);
            setSelectedPin(null);
            if (pendingPlacingPin) {
              setPlacingPin(pendingPlacingPin);
              setPendingPlacingPin(null);
            } else {
              setPlacingPin(null);
            }
          }}
        />
      )}
      {showTextModal && textPosition && (
        <TextModal
          position={textPosition}
          existingText={selectedText}
          onSave={(textData) => {
            if (selectedText) {
              // Editing existing text
              setShapes(shapes => shapes.map(shape =>
                shape.id === selectedText.id
                  ? { ...shape, ...textData, color }
                  : shape
              ));
            } else {
              // Adding new text
              setShapes(shapes => [...shapes, { type: 'text', ...textData, color }]);
            }
            setShowTextModal(false);
            setTextPosition(null);
            setSelectedText(null);
          }}
          onClose={() => {
            setShowTextModal(false);
            setTextPosition(null);
            setSelectedText(null);
          }}
        />
      )}
    </div>
  );
}

export default ImageEditor;

// Helper for dropdown label
function dropdownLabel(value) {
  if (value === 'option1') return 'Option 1';
  if (value === 'option2') return 'Option 2';
  if (value === 'option3') return 'Option 3';
  return value;
}
